import { useEffect, useRef, useState } from "react";
import { getNodeCG } from "./client.js";

/**
 * Subscribe to a slice of a replicant. The selector runs on every change;
 * we only re-render when the selected value differs (by `Object.is` or a
 * provided equality fn). Useful for "the projects replicant has many slugs
 * but I only care about this one".
 */
export function useReplicantSelector<T, U>(
  name: string,
  selector: (value: T | undefined) => U,
  equals: (a: U, b: U) => boolean = Object.is,
): U {
  const selectorRef = useRef(selector);
  selectorRef.current = selector;

  const [slice, setSlice] = useState<U>(() => {
    const nodecg = getNodeCG();
    const initial = nodecg ? nodecg.Replicant<T>(name).value : undefined;
    return selectorRef.current(initial);
  });

  useEffect(() => {
    const nodecg = getNodeCG();
    if (!nodecg) return;
    const rep = nodecg.Replicant<T>(name);
    let prev = selectorRef.current(rep.value);
    setSlice(prev);
    const handler = (newVal: T | undefined) => {
      const next = selectorRef.current(newVal);
      if (!equals(prev, next)) {
        prev = next;
        setSlice(next);
      }
    };
    rep.on("change", handler);
    return () => {
      rep.removeListener?.("change", handler);
    };
  }, [name, equals]);

  return slice;
}
